// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AssistantChat from './AssistantChat';
import { SignatureModal } from './SignatureModal';
import TravelerVisitDialog from './TravelerVisitDialog';
import { useTour } from '../hooks/useTour';
import { AREA_CONFIG, DEFAULT_AREA } from './layout/areaConfig';
import Sidebar from './layout/Sidebar';
import Topbar from './layout/Topbar';
import '../styles/tour.css';

/**
 * App shell: sidebar + topbar + main content slot + floating helpers
 * (assistant chat, signature modal). Stateful logic that wires UI together
 * lives here; presentation lives inside Sidebar/Topbar.
 */
export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [travelerVisitOpen, setTravelerVisitOpen] = useState(false);
  const isAdminUser = isAdmin();
  const { startTour, hasSeenTour } = useTour(isAdminUser);

  // Auto-start tour for first-time users landing on /dashboard.
  useEffect(() => {
    if (!hasSeenTour() && location.pathname === '/dashboard') {
      const timer = setTimeout(() => {
        setSidebarOpen(false);
        startTour();
      }, 800);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedArea = localStorage.getItem('selectedArea') || DEFAULT_AREA;
  const currentArea = AREA_CONFIG[selectedArea];

  const handleLogout = () => {
    localStorage.removeItem('selectedArea');
    logout();
    navigate('/');
  };

  const handleChangeArea = () => navigate('/select-area');

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/20 dark:bg-black/50 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        user={user}
        isAdminUser={isAdminUser}
        currentArea={currentArea}
        onChangeArea={handleChangeArea}
        onOpenTravelerVisit={() => setTravelerVisitOpen(true)}
      />

      <div className="lg:pl-72">
        <Topbar
          user={user}
          isAdminUser={isAdminUser}
          currentArea={currentArea}
          onOpenSidebar={() => setSidebarOpen(true)}
          onStartTour={startTour}
          onOpenSignature={() => setSignatureModalOpen(true)}
          onChangeArea={handleChangeArea}
          onLogout={handleLogout}
        />

        <main className="p-4 lg:p-8">{children}</main>
      </div>

      <SignatureModal open={signatureModalOpen} onOpenChange={setSignatureModalOpen} />
      <TravelerVisitDialog open={travelerVisitOpen} onOpenChange={setTravelerVisitOpen} />
      <AssistantChat />
    </div>
  );
}
